import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Snackbar,
  Button, Stack, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  useTheme, Paper, Chip,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, ExpandMore, Schedule } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch, store } from 'state/store';
import { fetchCompanyDetailsAsync } from 'state/authentication/authenticationSlice';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { trackAction } from 'utils/Infralytiqs';

import {
  BusinessPlanFromAPI, CurrencyInfo, COUNTRY_TO_CURRENCY,
  LICENSE_TO_PLAN_KEY, PLAN_QUOTAS,
  getActivePlan, extractCurrentQuotas, isAnnualCommitment,
  formatPrice, formatDate, DOWNGRADE_PENALTY_RATE,
} from './types';
import { CurrentPlanSection } from './CurrentPlanSection';
import { UpgradeSection } from './UpgradeSection';
import { DowngradeSection } from './DowngradeSection';

export const Plan: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const company = useSelector((state: RootState) => state.authentication.company);
  const user = useSelector((state: RootState) => state.authentication.user);

  const [plans, setPlans] = useState<BusinessPlanFromAPI[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<string | null>(null);
  const [selectedDowngradePlan, setSelectedDowngradePlan] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [showRenewConfirm, setShowRenewConfirm] = useState(false);

  const selectedCurrency = useMemo(() => {
    if (company?.currency) return company.currency.toUpperCase();
    const countryCode = (company?.country || '').toUpperCase();
    return COUNTRY_TO_CURRENCY[countryCode] || 'GBP';
  }, [company]);

  // ── Fetch available business plans ──────────────────────────────────────────

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const hostname = window.location.hostname;
        const hostParts = hostname.split('.');
        const rootDomain = hostParts.length >= 2 ? hostParts.slice(-2).join('.') : hostname;
        const countryCode = (company?.country || '').toUpperCase();

        let response;
        if (countryCode) {
          response = await apiClient.get(`/auth/signup-plans?country=${encodeURIComponent(countryCode)}&rootDomain=${encodeURIComponent(rootDomain)}`);
        } else {
          response = await apiClient.get('/auth/business-plans');
        }
        if (response.data) {
          setPlans(response.data.plans || []);
          setCurrencies(response.data.supportedCurrencies || []);
        }
      } catch (err: any) {
        Logger.error('Plan', 'Failed to fetch business plans: ' + err.message);
        setError('Failed to load business plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [company?.country]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const activePlan = useMemo(() => {
    if (!company?.productPlans) return null;
    return getActivePlan(company.productPlans);
  }, [company]);

  const currentPlanKey = useMemo(() => {
    if (company?.businessPlanKey && plans.find(p => p.key === company.businessPlanKey)) {
      return company.businessPlanKey;
    }
    if (!activePlan) return 'starter';
    const mappedKey = LICENSE_TO_PLAN_KEY[activePlan.licenseType] || 'starter';
    if (plans.find(p => p.key === mappedKey)) return mappedKey;
    const matchByLicense = plans.find(p =>
      (p.licenseTypes || []).includes(activePlan.licenseType)
    );
    if (matchByLicense) return matchByLicense.key;
    const matchByLabel = plans.find(p =>
      p.label.toLowerCase().replace(/\s+/g, '-') === mappedKey
    );
    if (matchByLabel) return matchByLabel.key;
    return mappedKey;
  }, [activePlan, plans, company]);

  const currentPlanDef = useMemo(
    () => plans.find(p => p.key === currentPlanKey) || null,
    [plans, currentPlanKey]
  );

  const currentQuotas = useMemo(() => {
    if (!activePlan) return PLAN_QUOTAS['starter'];
    return extractCurrentQuotas(activePlan);
  }, [activePlan]);

  const companyBillingCycle: 'monthly' | 'annual' = useMemo(
    () => (company?.billingCycle as 'monthly' | 'annual') || 'monthly',
    [company]
  );

  const currencySymbol = useMemo(() => {
    const c = currencies.find(c => c.code === selectedCurrency);
    return c?.symbol || '£';
  }, [currencies, selectedCurrency]);

  const isAnnual = companyBillingCycle === 'annual';
  const autoRenewalCancelled = !!company?.autoRenewalCancelled;

  const allUpgradeRoutes = useMemo(
    () => currentPlanDef?.upgradeRoutes?.[companyBillingCycle] || [],
    [currentPlanDef, companyBillingCycle]
  );

  const allDowngradeRoutes = useMemo(
    () => currentPlanDef?.downgradeRoutes?.[companyBillingCycle] || [],
    [currentPlanDef, companyBillingCycle]
  );

  useEffect(() => {
    if (allUpgradeRoutes.length === 0) return;
    const hasMonthly = allUpgradeRoutes.some(r => r.targetCycle === 'monthly');
    if (!hasMonthly && billingCycle === 'monthly') {
      setBillingCycle('annual');
    }
  }, [allUpgradeRoutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const findPlanByRouteTarget = useCallback(
    (targetPlan: string) => {
      const normalized = targetPlan.toLowerCase().replace(/\s+/g, '-');
      return plans.find(p => p.key === normalized)
        || plans.find(p => p.key === targetPlan)
        || plans.find(p => p.label.toLowerCase() === targetPlan.toLowerCase());
    },
    [plans]
  );

  const upgradePlans = useMemo(
    () => allUpgradeRoutes
      .filter(r => r.targetCycle === billingCycle)
      .map(r => findPlanByRouteTarget(r.targetPlan))
      .filter(Boolean) as BusinessPlanFromAPI[],
    [allUpgradeRoutes, billingCycle, findPlanByRouteTarget]
  );

  const downgradePlans = useMemo(
    () => allDowngradeRoutes
      .map(r => ({ plan: findPlanByRouteTarget(r.targetPlan)!, cycle: r.targetCycle }))
      .filter(r => r.plan),
    [allDowngradeRoutes, findPlanByRouteTarget]
  );

  const selectedUpgradeDef = useMemo(
    () => plans.find(p => p.key === selectedUpgradePlan) || null,
    [plans, selectedUpgradePlan]
  );

  const selectedDowngradeRoute = useMemo(() => {
    if (!selectedDowngradePlan) return null;
    const normalizedTarget = selectedDowngradePlan.toLowerCase().replace(/\s+/g, '-');
    const route = allDowngradeRoutes.find(r =>
      r.targetPlan.toLowerCase().replace(/\s+/g, '-') === normalizedTarget
    );
    const plan = plans.find(p => p.key === normalizedTarget) || plans.find(p => p.key === selectedDowngradePlan);
    return route && plan ? { plan, cycle: route.targetCycle } : null;
  }, [selectedDowngradePlan, allDowngradeRoutes, plans]);

  const nextRenewalDate = useMemo(() => {
    if (activePlan?.endsAt) return formatDate(activePlan.endsAt);
    return 'N/A';
  }, [activePlan]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const getPrice = useCallback(
    (plan: BusinessPlanFromAPI, cycle: 'monthly' | 'annual'): number => {
      const currPricing = plan.pricing?.[selectedCurrency];
      if (currPricing) {
        return cycle === 'annual' ? currPricing.annualMonthlyPrice : currPricing.monthlyPrice;
      }
      return cycle === 'annual' ? plan.annualMonthlyPriceInPence : plan.monthlyPriceInPence;
    },
    [selectedCurrency]
  );

  const handleCancelSubscription = useCallback(async () => {
    try {
      setCancelLoading(true);
      setActionError(null);
      setShowCancelConfirm(false);
      const response = await apiClient.post('/payment/cancel-subscription', {});
      if (response.data?.message) {
        setActionSuccess(response.data.message);
      }
      trackAction('subscription_cancelled', undefined, {
        current_plan: (company as any)?.businessPlan,
        company_id: company?._id,
        user_email: user?.email,
      });
      if (company?._id) dispatch(fetchCompanyDetailsAsync(company._id));
    } catch (err: any) {
      setActionError(err.message || 'Failed to cancel subscription.');
    } finally {
      setCancelLoading(false);
    }
  }, [company, dispatch, user]);

  const handleRenewSubscription = useCallback(async () => {
    try {
      setRenewLoading(true);
      setActionError(null);
      setShowRenewConfirm(false);
      const response = await apiClient.post('/payment/renew-subscription', {});
      if (response.data?.message) {
        setActionSuccess(response.data.message);
      }
      trackAction('subscription_renewed', undefined, {
        current_plan: (company as any)?.businessPlan,
        company_id: company?._id,
        user_email: user?.email,
      });
      if (company?._id) dispatch(fetchCompanyDetailsAsync(company._id));
    } catch (err: any) {
      setActionError(err.message || 'Failed to renew subscription.');
    } finally {
      setRenewLoading(false);
    }
  }, [company, dispatch, user]);

  const handleUpgrade = useCallback(async () => {
    if (!selectedUpgradePlan) return;
    try {
      setUpgradeLoading(true);
      setActionError(null);
      const response = await apiClient.post('/payment/upgrade-plan', {
        planKey: selectedUpgradePlan, billingCycle,
      });
      // Fire the event regardless of whether a Stripe redirect follows — the
      // user's intent is "upgrade initiated". `navigator.sendBeacon` inside the
      // SDK makes this robust across the page unload.
      trackAction('plan_upgrade_initiated', selectedUpgradePlan, {
        target_plan: selectedUpgradePlan,
        billing_cycle: billingCycle,
        current_plan: (company as any)?.businessPlan,
        has_checkout_redirect: !!response.data?.checkoutUrl,
        company_id: company?._id,
        user_email: user?.email,
      });
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else if (response.data?.message) {
        setActionSuccess(response.data.message);
        setSelectedUpgradePlan(null);
        if (company?._id) dispatch(fetchCompanyDetailsAsync(company._id));
      } else {
        setActionError('Unexpected response from payment server.');
      }
    } catch (err: any) {
      setActionError(err.message || 'Failed to initiate upgrade. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  }, [selectedUpgradePlan, billingCycle, company, dispatch, user]);

  const handleDowngradeConfirm = useCallback(async () => {
    if (!selectedDowngradePlan) return;
    try {
      setDowngradeLoading(true);
      setActionError(null);
      setShowDowngradeConfirm(false);
      const response = await apiClient.post('/payment/downgrade-plan', {
        planKey: selectedDowngradePlan,
      });
      if (response.data?.message) {
        let msg = response.data.message;
        if (response.data.penaltyCharged) {
          msg += ` An early termination fee of ${formatPrice(response.data.penaltyAmount, currencySymbol)} was charged.`;
        }
        trackAction('plan_downgraded', selectedDowngradePlan, {
          target_plan: selectedDowngradePlan,
          current_plan: (company as any)?.businessPlan,
          penalty_charged: !!response.data.penaltyCharged,
          penalty_amount: response.data.penaltyAmount,
          company_id: company?._id,
          user_email: user?.email,
        });
        setActionSuccess(msg);
        setSelectedDowngradePlan(null);
        if (company?._id) dispatch(fetchCompanyDetailsAsync(company._id));
      }
    } catch (err: any) {
      setActionError(err.message || 'Failed to process downgrade. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  }, [selectedDowngradePlan, company, dispatch, currencySymbol, user]);

  // ── Checkout callback verification (for Starter upgrade) ──────────────────

  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/[?&]upgrade_session_id=([^&]+)/);
    if (!match) return;
    const sessionId = match[1];
    const cleanHash = hash.replace(/[?&]upgrade_session_id=[^&]+/, '').replace(/\?$/, '');
    window.location.hash = cleanHash;
    const verifyUpgradePayment = async () => {
      try {
        setActionError(null);
        const response = await apiClient.post('/payment/verify-upgrade', { sessionId });
        if (response.data?.message) setActionSuccess(response.data.message);
        trackAction('plan_upgrade_completed', undefined, {
          session_id: sessionId,
          company_id: (store.getState() as RootState).authentication.company?._id,
        });
        setSelectedUpgradePlan(null);
        setSelectedDowngradePlan(null);
      } catch (err: any) {
        setActionError(err.message || 'Failed to verify upgrade payment.');
      }
      const currentCompany = (store.getState() as RootState).authentication.company;
      if (currentCompany?._id) {
        await dispatch(fetchCompanyDetailsAsync(currentCompany._id));
      }
    };
    verifyUpgradePayment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading / Error States ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Company data not available. Please try again later.</Alert>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Plan Management</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View your current plan details and manage your subscription.
      </Typography>

      <Snackbar open={!!actionError} autoHideDuration={6000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setActionError(null)} sx={{ width: '100%' }}>
          {actionError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!actionSuccess} autoHideDuration={6000}
        onClose={() => setActionSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setActionSuccess(null)} sx={{ width: '100%' }}>
          {actionSuccess}
        </Alert>
      </Snackbar>

      {/* Pending Plan Change Banner */}
      {company?.pendingPlanChange && (
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            p: 2,
            borderLeft: '4px solid',
            borderColor: company.pendingPlanChange.type === 'upgrade' ? 'success.main' : 'warning.main',
            bgcolor: company.pendingPlanChange.type === 'upgrade'
              ? 'rgba(56, 142, 60, 0.06)'
              : 'rgba(239, 108, 0, 0.06)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Schedule color={company.pendingPlanChange.type === 'upgrade' ? 'success' : 'warning'} />
            <Typography variant="body1" fontWeight={600}>
              Pending {company.pendingPlanChange.type === 'upgrade' ? 'Upgrade' : 'Downgrade'}
            </Typography>
            <Chip
              label={`${company.pendingPlanChange.targetPlanLabel} (${company.pendingPlanChange.targetBillingCycle === 'annual' ? 'Annual' : 'Monthly'})`}
              color={company.pendingPlanChange.type === 'upgrade' ? 'success' : 'warning'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              — will take effect when Stripe collects payment in your next billing cycle.
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Section 1: Current Plan */}
      <CurrentPlanSection
        company={company}
        currentPlanKey={currentPlanKey}
        currentPlanDef={currentPlanDef}
        hasAnnualCommitment={isAnnual}
        activePlan={activePlan}
        isAnnual={isAnnual}
        nextRenewalDate={nextRenewalDate}
        autoRenewalCancelled={autoRenewalCancelled}
        onShowCancelConfirm={() => setShowCancelConfirm(true)}
        onShowRenewConfirm={() => setShowRenewConfirm(true)}
      />

      {/* Section 2: Upgrade */}
      {allUpgradeRoutes.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' }, overflow: 'hidden' }}
          elevation={2}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ bgcolor: '#388e3c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, width: 32, height: 32, flexShrink: 0 }}>
                <ArrowUpward sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Upgrade Your Plan
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            <UpgradeSection
              currentPlanKey={currentPlanKey}
              currentPlanDef={currentPlanDef}
              currentQuotas={currentQuotas}
              companyBillingCycle={companyBillingCycle}
              billingCycle={billingCycle}
              setBillingCycle={setBillingCycle}
              upgradePlans={upgradePlans}
              selectedUpgradePlan={selectedUpgradePlan}
              setSelectedUpgradePlan={setSelectedUpgradePlan}
              selectedUpgradeDef={selectedUpgradeDef}
              currencySymbol={currencySymbol}
              upgradeLoading={upgradeLoading}
              getPrice={getPrice}
              onUpgrade={handleUpgrade}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Section 3: Downgrade */}
      {allDowngradeRoutes.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{ borderRadius: '8px !important', mb: 2, '&:before': { display: 'none' }, overflow: 'hidden' }}
          elevation={2}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ bgcolor: '#ef6c00', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, width: 32, height: 32, flexShrink: 0 }}>
                <ArrowDownward sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Downgrade Plan
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
            <DowngradeSection
              hasAnnualCommitment={isAnnual}
              downgradePlans={downgradePlans}
              selectedDowngradePlan={selectedDowngradePlan}
              setSelectedDowngradePlan={setSelectedDowngradePlan}
              selectedDowngradeRoute={selectedDowngradeRoute}
              currentQuotas={currentQuotas}
              currentPlanDef={currentPlanDef}
              currencySymbol={currencySymbol}
              downgradeLoading={downgradeLoading}
              getPrice={getPrice}
              onShowDowngradeConfirm={() => setShowDowngradeConfirm(true)}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? Your plan will remain active until the end of
            the current billing period, after which Stripe will stop collecting payments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={() => setShowCancelConfirm(false)}>Keep Subscription</Button>
          <Button onClick={handleCancelSubscription} color="error" variant="contained" disabled={cancelLoading}>
            {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Subscription Dialog */}
      <Dialog open={showRenewConfirm} onClose={() => setShowRenewConfirm(false)}>
        <DialogTitle>Renew Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Would you like to renew your subscription? Stripe will resume collecting payments automatically
            from your next billing cycle.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={() => setShowRenewConfirm(false)}>Not Now</Button>
          <Button onClick={handleRenewSubscription} color="success" variant="contained" disabled={renewLoading}>
            {renewLoading ? 'Renewing...' : 'Renew Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog open={showDowngradeConfirm} onClose={() => setShowDowngradeConfirm(false)}>
        <DialogTitle>Confirm Downgrade</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to downgrade to <strong>{selectedDowngradeRoute?.plan?.label}</strong>{' '}
            ({selectedDowngradeRoute?.cycle === 'annual' ? 'Annual' : 'Monthly'})?
            New pricing will take effect from your next billing cycle.
          </DialogContentText>
          {isAnnual && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Since you are on an annual commitment, an early termination fee of 20% of the annual plan cost
              may apply if more than 3 months remain in your commitment period.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={() => setShowDowngradeConfirm(false)}>Cancel</Button>
          <Button onClick={handleDowngradeConfirm} color="warning" variant="contained" disabled={downgradeLoading}>
            {downgradeLoading ? 'Processing...' : 'Confirm Downgrade'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
